import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import { useLazyQueryDebounce } from 'modules/api';
import { useConnection } from 'modules/auth';
import {
  getFirstError,
  MIN_STAKE_VALUE,
  NumberTextField,
  ONE,
  ZERO,
} from 'modules/common';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';
import {
  useGetBalancesQuery,
  useLazyGetStakeGasEstimationQuery,
  useUnstakeMutation,
} from 'modules/stake';

import { useGetStatsQuery } from '../../actions/getStats';
import { ActionButton } from './ActionButton';
import { Details } from './Details';
import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

interface IFormValues extends FieldValues {
  amount: string;
}

export function UnstakeForm(): ReactElement {
  const { classes } = useStyles();
  const { t, keys } = useTranslation(mergedTranslation);
  const [unstake, { isLoading }] = useUnstakeMutation();
  const [isMax, setIsMax] = useState(false);

  const { isConnected } = useConnection();

  const { data } = useGetBalancesQuery(undefined, {
    skip: !isConnected,
  });

  const balanceStaked = data?.staked ?? ZERO;

  const { data: stats } = useGetStatsQuery();

  const {
    control,
    formState: { errors, isValid, isValidating },
    handleSubmit,
    watch,
    setValue,
    reset,
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      amount: '',
    },
  });

  const amountInputValue = watch('amount');

  const convertedAmount = useMemo(() => {
    const convertedValue = new BigNumber(amountInputValue);

    return convertedValue.isNaN() ? ZERO : convertedValue;
  }, [amountInputValue]);

  const iotaUsdPrice = stats?.iotaUsdPrice ?? ZERO;
  const rate = stats?.rate ?? ONE;
  const amountUsd = convertedAmount
    .div(rate.isZero() ? ONE : rate)
    .multipliedBy(iotaUsdPrice);

  const isSubmitDisabled = !isValid || isLoading;

  const onSubmit = async ({ amount }: { amount: BigNumber }) => {
    const { error } = await unstake({ amount });
    if (!error) {
      reset();
    }
  };

  const [getStakeGasEstimation, { currentData: gasEstimationCurrentData }] =
    useLazyQueryDebounce(useLazyGetStakeGasEstimationQuery);

  useEffect(() => {
    if (isConnected && isValid && !isValidating && amountInputValue) {
      void getStakeGasEstimation({ amount: amountInputValue }, true);
    }
  }, [
    amountInputValue,
    getStakeGasEstimation,
    isConnected,
    isValid,
    isValidating,
  ]);

  const onMaxClick = useCallback(() => {
    if (!data?.staked) {
      return;
    }

    if (data.staked.isLessThanOrEqualTo(0)) {
      setValue('amount', '0', { shouldValidate: true });
      return;
    }

    setValue('amount', data.staked.toString(), { shouldValidate: true });
    setIsMax(true);
  }, [data, setValue]);

  const handleChange = useCallback(() => {
    setIsMax(false);
  }, []);

  return (
    <form
      onChange={handleChange}
      onSubmit={handleSubmit(({ amount }) =>
        onSubmit({ amount: new BigNumber(amount) }),
      )}
    >
      <div className={classes.balanceWrap}>
        <div className={classes.amount}>{t(keys.amount)}</div>

        {balanceStaked && (
          <div>
            {t(keys.balance, {
              value: balanceStaked?.decimalPlaces(4).toFormat(),
              token: t(keys.tokens.staked),
            })}
          </div>
        )}
      </div>

      <NumberTextField
        amountUsd={amountUsd}
        balance={balanceStaked}
        control={control as unknown as Control<FieldValues>}
        disabled={isLoading}
        error={getFirstError(errors)}
        isStakeTab={false}
        maxButtonProps={{
          disabled: !data?.staked || isMax,
        }}
        min={MIN_STAKE_VALUE}
        name="amount"
        placeholder="0"
        unit={t(keys.tokens.staked)}
        variant="outlined"
        onMaxClick={onMaxClick}
      />

      <div className={classes.action}>
        <ActionButton
          isLoading={isLoading}
          isSubmitDisabled={isSubmitDisabled}
          isZeroAmount={convertedAmount.isZero()}
        />
      </div>

      <Details
        amount={convertedAmount}
        fee={gasEstimationCurrentData?.estimatedGas}
        isStakeTab={false}
        staked={balanceStaked}
      />
    </form>
  );
}

const useStyles = makeStyles()(theme => ({
  balanceWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 14,
    marginTop: theme.spacing(7),

    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(6),
    },
  },
  amount: {
    fontWeight: 600,
  },

  action: {
    marginTop: theme.spacing(9),
    width: '100%',
  },

  loader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },
}));
